import { useUser } from '@dofleini/security';
import { partner } from '@mep/pae-commons';
import { Button, Form } from 'antd';
import FormBuilder from 'antd-form-builder';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { EntityDetailContent } from '@/components/EntityDetailContent';
import FloatContainer, { TYPE_CONTAINER } from '@/components/FloatContainer';
import { useDialogsContext } from '@/contexts/DialogsContext';
import { useResponsive } from '@/contexts/ResponsiveContext';

import { DIALOG_PARTNER_GOODS_NAMESPACE, useFields } from './constants/goods';

const { CIVIL_STATUS } = partner;

const GoodsCreateForm = ({ onConfirm, index, owner, hideCommunityProperty }) => {
  const { t } = useTranslation('proposalRequest');
  const { isOpen, closeDialog, payload } = useDialogsContext(`${DIALOG_PARTNER_GOODS_NAMESPACE}_${index}`);
  const { isMobile } = useResponsive();
  const initValues = Object.assign({}, payload?.data || {});
  const { user } = useUser();
  const [form] = Form.useForm();
  const forceUpdate = FormBuilder.useForceUpdate();
  const fields = useFields(false, form, 3);
  
  const isMarried = useMemo(() => !hideCommunityProperty && (owner || user)?.civilStatus === CIVIL_STATUS.MARRIED, [hideCommunityProperty, owner, user]);
  
  useEffect(() => {
    isOpen && form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  
  const onCLose = useCallback(() => {
    closeDialog();
    form.resetFields();
  }, [closeDialog, form]);
  
  const handleSubmit = useCallback(async (values) => {
    await onConfirm(values, payload?.isEdit, payload?.recordIndex);
    onCLose();
  }, [onConfirm, payload, onCLose]);
  
  useDeepCompareEffect(() => {
    if (!isEmpty(initValues)) {
      form.setFieldsValue(initValues);
    }
  }, [initValues]);
  
  return (
    <FloatContainer
      type={isMobile ? TYPE_CONTAINER.DRAWER : TYPE_CONTAINER.MODAL}
      visible={isOpen}
      title={null}
      closable
      bodyStyle={{ padding: '0.1rem 1.5rem' }}
      onClose={onCLose}
      width={700}
      footer={[
        <Button className="mx-2" key="back" onClick={onCLose}>
          {t('cancel')}
        </Button>,
        <Button className="mx-2" key="submit" type="primary" onClick={() => form.submit()}>
          {t('confirm')}
        </Button>
      ]}>
      <Form
        scrollToFirstError
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        onValuesChange={forceUpdate}
        initialValues={initValues}>
        <EntityDetailContent
          viewMode={false}
          form={form}
          data={initValues}
          fields={fields.filter((e) => {
            if (e?.key === 'isCommon') return isMarried;
            return true;
          })}
          columns={3}/>
      </Form>
    </FloatContainer>
  );
  
};

GoodsCreateForm.propTypes = {
  owner: PropTypes.object,
  hideCommunityProperty: PropTypes.object,
  index: PropTypes.number,
  onConfirm: PropTypes.func
};

GoodsCreateForm.defaultProps = {
  onConfirm: (c) => c
};

export default memo(GoodsCreateForm);
